import {
  createAudioPort,
  type AudioPort,
  type AudioCueId,
} from "@stormglass/game-audio-web";
import { launchContentPack } from "@stormglass/game-content";
import {
  createGameSimulation,
  type DomainEvent,
  type GameCommand,
  type GameSimulation,
  type GameSnapshot,
  type JsonValue,
  type SimulationCheckpoint,
  type UiProjection,
} from "@stormglass/game-core";
import {
  CURRENT_SAVE_SCHEMA_VERSION,
  IndexedDBSaveRepository,
  type JsonValue as PersistenceJsonValue,
  type SaveEnvelope,
} from "@stormglass/game-persistence";
import type {
  RenderEvent,
  RendererPort,
  RenderSnapshot,
} from "@stormglass/game-renderer-pixi";
import type { TelemetryPort } from "@stormglass/game-telemetry";

import { createAppTelemetry } from "./telemetry";

export interface AppSettings {
  readonly quality: "auto" | "high" | "balanced" | "low";
  readonly reducedMotion: boolean;
  readonly highContrast: boolean;
  readonly damageNumbers: "all" | "critical" | "off";
  readonly masterVolume: number;
  readonly musicVolume: number;
  readonly effectsVolume: number;
  readonly haptics: boolean;
  readonly analytics: boolean;
  readonly crashReports: boolean;
}

export const DEFAULT_SETTINGS: AppSettings = {
  quality: "auto",
  reducedMotion: globalThis.matchMedia("(prefers-reduced-motion: reduce)")
    .matches,
  highContrast: false,
  damageNumbers: "all",
  masterVolume: 0.75,
  musicVolume: 0.25,
  effectsVolume: 0.7,
  haptics: true,
  analytics: false,
  crashReports: false,
};

export interface HostState {
  readonly snapshot: Readonly<GameSnapshot>;
  readonly ui: Readonly<UiProjection>;
  readonly settings: AppSettings;
  readonly audioUnlocked: boolean;
  readonly recoveredFromBackup: boolean;
  readonly storageError: string | null;
}

type Listener = () => void;

const RELEASE = {
  appVersion: "1.0.0",
  simulationVersion: 1,
};

const asNumber = (value: JsonValue | undefined): number | undefined =>
  typeof value === "number" ? value : undefined;
const asString = (value: JsonValue | undefined): string | undefined =>
  typeof value === "string" ? value : undefined;

const eventPosition = (
  event: DomainEvent,
  prefix: "source" | "target",
): { x?: number; y?: number } => {
  const directX = asNumber(event.payload[`${prefix}X`]);
  const directY = asNumber(event.payload[`${prefix}Y`]);
  return {
    ...(directX === undefined ? {} : { x: directX }),
    ...(directY === undefined ? {} : { y: directY }),
  };
};

const eventToRenderEvent = (
  event: DomainEvent,
  snapshot: Readonly<GameSnapshot>,
): RenderEvent | null => {
  const targetId =
    asString(event.payload["enemyId"]) ?? asString(event.payload["targetId"]);
  const sourceId = asString(event.payload["sourceId"]);
  const target = snapshot.enemies.find((enemy) => enemy.id === targetId);
  const source = snapshot.enemies.find((enemy) => enemy.id === sourceId);
  const directSource = eventPosition(event, "source");
  const directTarget = eventPosition(event, "target");
  const normalizedType = event.type.toLowerCase();
  if (
    ![
      "damage",
      "attack",
      "weapon",
      "breach",
      "impact",
      "reaction",
      "lightning",
    ].some((part) => normalizedType.includes(part))
  )
    return null;
  const weather =
    asString(event.payload["weather"]) ?? asString(event.payload["damageType"]);
  const value =
    asNumber(event.payload["damage"]) ?? asNumber(event.payload["value"]);
  return {
    id: `${String(event.tick)}:${String(event.sequence)}`,
    type: event.type,
    sourceX: directSource.x ?? source?.position.x ?? snapshot.core.position.x,
    sourceY: directSource.y ?? source?.position.y ?? snapshot.core.position.y,
    targetX: directTarget.x ?? target?.position.x ?? snapshot.core.position.x,
    targetY: directTarget.y ?? target?.position.y ?? snapshot.core.position.y,
    ...(value === undefined ? {} : { value }),
    critical: event.payload["critical"] === true,
    ...(weather === "rain" ||
    weather === "wind" ||
    weather === "hail" ||
    weather === "lightning"
      ? { weather }
      : {}),
  };
};

const snapshotToRender = (
  snapshot: Readonly<GameSnapshot>,
  events: readonly DomainEvent[],
): RenderSnapshot => ({
  tick: snapshot.tick,
  width: 1000,
  height: 1400,
  integrity: snapshot.core.integrity,
  maxIntegrity: snapshot.core.maxIntegrity,
  shield: snapshot.core.shield,
  enemies: snapshot.enemies.map((enemy) => ({
    id: enemy.id,
    kind: enemy.definitionId,
    x: enemy.position.x,
    y: enemy.position.y,
    health: enemy.health,
    maxHealth: enemy.maxHealth,
    radius: enemy.radius,
    boss: enemy.isBoss,
    statuses: enemy.statuses.map((status) => status.statusId),
    affinity: enemy.affinityId,
  })),
  zones: snapshot.zones.map((zone) => ({
    id: zone.id,
    kind: zone.kind,
    x: zone.position.x,
    y: zone.position.y,
    radius: zone.radius,
    expiresAtTick: zone.expiresAtTick,
  })),
  events: events
    .map((event) => eventToRenderEvent(event, snapshot))
    .filter((event): event is RenderEvent => event !== null),
  weather: snapshot.ownedWeapons,
  bossActive: snapshot.boss !== null,
});

const cueForEvent = (event: DomainEvent): AudioCueId | null => {
  const type = event.type.toLowerCase();
  if (
    type.includes("boss") &&
    (type.includes("warning") || type.includes("phase"))
  )
    return "boss-warning";
  if (type.includes("reaction")) return "reaction";
  if (type.includes("breach") || type.includes("core-impact"))
    return "enemy-impact";
  if (type.includes("defeat") || type.includes("killed"))
    return "enemy-defeated";
  if (type.includes("upgrade-purchased")) return "purchase";
  if (type.includes("milestone")) return "milestone";
  if (
    type.includes("damage") ||
    type.includes("weapon-fired") ||
    type.includes("lightning")
  )
    return "lightning-strike";
  return null;
};

const safeSettings = (value: unknown): AppSettings => {
  if (typeof value !== "object" || value === null) return DEFAULT_SETTINGS;
  return { ...DEFAULT_SETTINGS, ...(value as Partial<AppSettings>) };
};

const savedStringArray = (value: JsonValue | undefined): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];

const savedNumberArray = (value: JsonValue | undefined): number[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is number => typeof entry === "number")
    : [];

const jsonObjectField = (
  value: JsonValue,
  key: string,
): JsonValue | undefined => {
  if (typeof value !== "object" || value === null || Array.isArray(value))
    return undefined;
  return (value as { readonly [name: string]: JsonValue })[key];
};

const downloadBlob = (blob: Blob, filename: string): void => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
  }, 1000);
};

export class GameHost {
  private simulation: GameSimulation;
  private readonly repository: IndexedDBSaveRepository;
  private readonly audio: AudioPort;
  private readonly listeners = new Set<Listener>();
  private renderer: RendererPort | undefined;
  private frameId: number | undefined;
  private previousTime = 0;
  private accumulatedMs = 0;
  private lastUiNotify = 0;
  private lastCheckpointAt = 0;
  private seed: number;
  private revision = 0;
  private state: HostState;
  private readonly analyticsTelemetry: TelemetryPort;
  private readonly crashTelemetry: TelemetryPort;

  private constructor(
    simulation: GameSimulation,
    repository: IndexedDBSaveRepository,
    settings: AppSettings,
    seed: number,
    recoveredFromBackup: boolean,
    storageError: string | null,
  ) {
    this.simulation = simulation;
    this.repository = repository;
    this.seed = seed;
    this.audio = createAudioPort();
    const telemetry = createAppTelemetry();
    this.analyticsTelemetry = telemetry.analytics;
    this.crashTelemetry = telemetry.crashes;
    void this.analyticsTelemetry.setConsent(settings.analytics);
    void this.crashTelemetry.setConsent(settings.crashReports);
    this.applyAudioSettings(settings);
    this.state = {
      snapshot: simulation.getSnapshot(),
      ui: simulation.getUiProjection(),
      settings,
      audioUnlocked: false,
      recoveredFromBackup,
      storageError,
    };
    document.addEventListener("visibilitychange", this.handleVisibility);
    this.analyticsTelemetry.track("app_started", {
      standalone: globalThis.matchMedia("(display-mode: standalone)").matches,
      offline: !navigator.onLine,
    });
  }

  static async create(): Promise<GameHost> {
    const repository = new IndexedDBSaveRepository();
    let save: SaveEnvelope | null = null;
    let storageError: string | null = null;
    try {
      save = await repository.load();
    } catch (error) {
      storageError =
        error instanceof Error
          ? error.message
          : "Local progression could not be loaded.";
    }
    const settings = safeSettings(save?.settings);
    const seed =
      save?.activeRun?.seed ?? Math.floor(Math.random() * 0xffff_ffff);
    const activeRunCompatible =
      save?.activeRun !== null &&
      save?.activeRun !== undefined &&
      save.activeRun.appVersion === RELEASE.appVersion &&
      save.activeRun.contentVersion === launchContentPack.contentVersion &&
      save.activeRun.simulationVersion === RELEASE.simulationVersion;
    if (
      save?.activeRun !== null &&
      save?.activeRun !== undefined &&
      !activeRunCompatible
    ) {
      storageError ??=
        "The previous run uses a different application or content version. Permanent progression was kept, and the run remains preserved in the local save.";
    }
    const checkpoint =
      !activeRunCompatible ||
      save?.activeRun === null ||
      save?.activeRun === undefined
        ? undefined
        : ({
            checkpointVersion: 1 as const,
            simulationVersion: launchContentPack.simulationVersion,
            contentVersion: launchContentPack.contentVersion,
            state: save.activeRun.payload as JsonValue,
          } satisfies SimulationCheckpoint);
    let simulation: GameSimulation;
    try {
      simulation = createGameSimulation({
        content: launchContentPack,
        seed,
        ...(checkpoint === undefined
          ? {
              permanentProgression: {
                condensate: asNumber(save?.progression["condensate"]) ?? 0,
                completedMilestones: savedNumberArray(
                  save?.progression["completedMilestones"],
                ),
                permanentAccess: savedStringArray(
                  save?.progression["permanentAccess"],
                ),
                permanentOwned: savedStringArray(
                  save?.progression["permanentOwned"],
                ),
              },
            }
          : { checkpoint }),
      });
    } catch {
      simulation = createGameSimulation({ content: launchContentPack, seed });
      storageError ??=
        "The saved run was incompatible and was preserved outside this new run.";
    }
    return new GameHost(
      simulation,
      repository,
      settings,
      seed,
      repository.getLastRecoveryReport().recoveredFromBackup,
      storageError,
    );
  }

  readonly subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): HostState => this.state;

  attachRenderer(renderer: RendererPort): void {
    this.renderer = renderer;
    renderer.configure(this.state.settings);
    renderer.present(snapshotToRender(this.state.snapshot, []));
  }

  detachRenderer(renderer: RendererPort): void {
    if (this.renderer === renderer) this.renderer = undefined;
  }

  startLoop(): void {
    if (this.frameId !== undefined) return;
    this.previousTime = performance.now();
    this.frameId = requestAnimationFrame(this.step);
  }

  stopLoop(): void {
    if (this.frameId !== undefined) cancelAnimationFrame(this.frameId);
    this.frameId = undefined;
  }

  async unlockAudio(): Promise<void> {
    await this.audio.unlock();
    this.state = { ...this.state, audioUnlocked: true };
    this.emit();
  }

  command(command: GameCommand): void {
    this.simulation.enqueue(command);
    if (command.type === "start-run") this.audio.play("ui-confirm");
    if (
      command.type === "purchase-upgrade" ||
      command.type === "purchase-permanent"
    )
      this.audio.play("purchase");
    if (
      command.type === "choose-milestone-upgrade" ||
      command.type === "choose-permanent-unlock"
    )
      this.audio.play("milestone");
    this.advance(1);
    void this.checkpoint(
      command.type === "purchase-permanent" ||
        command.type === "choose-permanent-unlock",
    );
  }

  startFreshRun(): void {
    this.seed = Math.floor(Math.random() * 0xffff_ffff);
    this.command({ type: "start-run", seed: this.seed });
    this.lastCheckpointAt = 0;
  }

  updateSettings(patch: Partial<AppSettings>): void {
    const settings = { ...this.state.settings, ...patch };
    this.state = { ...this.state, settings };
    this.applyAudioSettings(settings);
    void this.analyticsTelemetry.setConsent(settings.analytics);
    void this.crashTelemetry.setConsent(settings.crashReports);
    this.renderer?.configure(settings);
    document.documentElement.dataset["contrast"] = settings.highContrast
      ? "high"
      : "normal";
    document.documentElement.dataset["motion"] = settings.reducedMotion
      ? "reduced"
      : "full";
    this.emit();
    void this.checkpoint(true);
  }

  async exportDiagnostics(): Promise<void> {
    const blob = await this.repository.exportDiagnostics();
    downloadBlob(
      blob,
      `stormglass-diagnostics-${new Date().toISOString().slice(0, 10)}.json`,
    );
  }

  reportRendererFailure(error: unknown): void {
    this.analyticsTelemetry.track("renderer_initialization_failed", {
      renderer: "pixi-webgl",
      reason: "initialization-failed",
    });
    this.crashTelemetry.captureError(error, { renderer: "pixi-webgl" });
  }

  getStatistics(): ReturnType<GameSimulation["getStatistics"]> {
    return this.simulation.getStatistics();
  }

  dismissRecovery(): void {
    this.state = {
      ...this.state,
      recoveredFromBackup: false,
      storageError: null,
    };
    this.emit();
  }

  destroy(): void {
    this.stopLoop();
    document.removeEventListener("visibilitychange", this.handleVisibility);
    this.audio.destroy();
    void this.analyticsTelemetry.shutdown();
    void this.crashTelemetry.shutdown();
  }

  private readonly step = (time: number): void => {
    const elapsed = Math.min(100, time - this.previousTime);
    this.previousTime = time;
    this.accumulatedMs += elapsed;
    const ui = this.simulation.getUiProjection();
    if (
      !ui.paused &&
      ui.phase !== "idle" &&
      ui.phase !== "ended" &&
      ui.phase !== "milestone"
    ) {
      let tickBudget = 0;
      while (this.accumulatedMs >= 1000 / 30 && tickBudget < 5) {
        this.accumulatedMs -= 1000 / 30;
        tickBudget += 1;
      }
      if (tickBudget > 0) this.advance(tickBudget);
    } else {
      this.accumulatedMs = 0;
    }
    if (time - this.lastCheckpointAt > 12_000 && ui.phase !== "idle") {
      this.lastCheckpointAt = time;
      void this.checkpoint(false);
    }
    if (time - this.lastUiNotify > 125) {
      this.lastUiNotify = time;
      this.refreshState();
    }
    this.frameId = requestAnimationFrame(this.step);
  };

  private advance(count: number): void {
    this.simulation.advanceTicks(count);
    const snapshot = this.simulation.getSnapshot();
    const events = this.simulation.drainEvents();
    this.renderer?.present(snapshotToRender(snapshot, events));
    for (const event of events) {
      this.trackDomainEvent(event, snapshot);
      const cue = cueForEvent(event);
      if (cue !== null) this.audio.play(cue);
      if (
        this.state.settings.haptics &&
        typeof navigator.vibrate === "function" &&
        (event.type.includes("breach") || event.type.includes("boss-phase"))
      )
        navigator.vibrate(event.type.includes("breach") ? 24 : [24, 50, 24]);
    }
    this.refreshState();
  }

  private refreshState(): void {
    this.state = {
      ...this.state,
      snapshot: this.simulation.getSnapshot(),
      ui: this.simulation.getUiProjection(),
    };
    this.emit();
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private applyAudioSettings(settings: AppSettings): void {
    this.audio.setBusVolume("master", settings.masterVolume);
    this.audio.setBusVolume("music", settings.musicVolume);
    this.audio.setBusVolume("weather", settings.effectsVolume);
    this.audio.setBusVolume("enemy", settings.effectsVolume);
    this.audio.setBusVolume("ui", settings.effectsVolume);
  }

  private async checkpoint(force: boolean): Promise<void> {
    const snapshot = this.simulation.getSnapshot();
    if (!force && snapshot.phase === "idle") return;
    const now = new Date().toISOString();
    const checkpoint = this.simulation.createCheckpoint();
    this.revision += 1;
    const envelope: SaveEnvelope = {
      saveSchemaVersion: CURRENT_SAVE_SCHEMA_VERSION,
      gameVersion: RELEASE.appVersion,
      contentVersion: launchContentPack.contentVersion,
      simulationVersion: RELEASE.simulationVersion,
      createdAt: now,
      updatedAt: now,
      profile: {},
      progression: {
        condensate: snapshot.condensate,
        completedMilestones: savedNumberArray(
          jsonObjectField(checkpoint.state, "completedMilestones"),
        ),
        permanentAccess: [...snapshot.permanentAccess],
        permanentOwned: [...snapshot.permanentOwned],
      },
      settings: {
        quality: this.state.settings.quality,
        reducedMotion: this.state.settings.reducedMotion,
        highContrast: this.state.settings.highContrast,
        damageNumbers: this.state.settings.damageNumbers,
        masterVolume: this.state.settings.masterVolume,
        musicVolume: this.state.settings.musicVolume,
        effectsVolume: this.state.settings.effectsVolume,
        haptics: this.state.settings.haptics,
        analytics: this.state.settings.analytics,
        crashReports: this.state.settings.crashReports,
      },
      statistics: {
        enemiesDefeated: this.simulation.getStatistics().enemiesDefeated,
        bossesDefeated: this.simulation.getStatistics().bossesDefeated,
        damageBySource: { ...this.simulation.getStatistics().damageBySource },
        reactionsTriggered: {
          ...this.simulation.getStatistics().reactionsTriggered,
        },
        highestRound: this.simulation.getStatistics().highestRound,
      },
      activeRun:
        snapshot.phase === "idle" || snapshot.phase === "ended"
          ? null
          : {
              checkpointVersion: checkpoint.checkpointVersion,
              appVersion: RELEASE.appVersion,
              contentVersion: checkpoint.contentVersion,
              simulationVersion: RELEASE.simulationVersion,
              seed: this.seed,
              tick: snapshot.tick,
              lastCompletedRound: Math.max(0, snapshot.round - 1),
              checkpointedAt: now,
              payload: checkpoint.state as PersistenceJsonValue,
            },
      integrity: { revision: this.revision, lastWriteId: crypto.randomUUID() },
    };
    try {
      await this.repository.save(envelope);
    } catch (error) {
      this.crashTelemetry.captureError(error, { operation: "save" });
      this.state = {
        ...this.state,
        storageError:
          error instanceof Error
            ? error.message
            : "Progress could not be saved.",
      };
      this.emit();
    }
  }

  private trackDomainEvent(
    event: DomainEvent,
    snapshot: Readonly<GameSnapshot>,
  ): void {
    if (event.type === "run:started") {
      this.analyticsTelemetry.track("run_started", {
        seed: asNumber(event.payload["seed"]) ?? this.seed,
        ownedWeatherCount: snapshot.ownedWeapons.length,
      });
    } else if (event.type === "round:completed") {
      this.analyticsTelemetry.track("round_completed", {
        round: asNumber(event.payload["round"]) ?? snapshot.round,
        elapsedTicks: snapshot.tick,
      });
    } else if (event.type === "boss:started") {
      this.analyticsTelemetry.track("boss_started", {
        bossType: asString(event.payload["bossId"]) ?? "unknown",
        round: snapshot.round,
      });
    } else if (event.type === "boss:defeated") {
      this.analyticsTelemetry.track("boss_defeated", {
        bossType: asString(event.payload["bossId"]) ?? "unknown",
        round: snapshot.round,
        ticksRemaining: snapshot.boss?.remainingTicks ?? 0,
      });
    } else if (event.type === "run:ended") {
      this.analyticsTelemetry.track("run_ended", {
        round: asNumber(event.payload["round"]) ?? snapshot.round,
        reason: asString(event.payload["reason"]) ?? "unknown",
        elapsedTicks: snapshot.tick,
      });
    } else if (event.type === "upgrade:purchased") {
      this.analyticsTelemetry.track("upgrade_purchased", {
        upgradeId: asString(event.payload["upgradeId"]) ?? "unknown",
        level: asNumber(event.payload["level"]) ?? 0,
        cost: asNumber(event.payload["cost"]) ?? 0,
      });
    } else if (event.type === "upgrade:chosen") {
      this.analyticsTelemetry.track("milestone_upgrade_selected", {
        upgradeId: asString(event.payload["upgradeId"]) ?? "unknown",
        round: snapshot.round,
      });
    } else if (event.type === "permanent:access-granted") {
      this.analyticsTelemetry.track("weather_unlocked", {
        weatherId: asString(event.payload["id"]) ?? "unknown",
        round: snapshot.round,
      });
    } else if (event.type === "permanent:purchased") {
      this.analyticsTelemetry.track("weather_purchased", {
        weatherId: asString(event.payload["id"]) ?? "unknown",
        cost: asNumber(event.payload["cost"]) ?? 0,
      });
    } else if (event.type === "reaction:triggered") {
      this.analyticsTelemetry.track("reaction_triggered", {
        reactionId: asString(event.payload["reactionId"]) ?? "unknown",
        round: snapshot.round,
      });
    }
  }

  private readonly handleVisibility = (): void => {
    if (document.visibilityState === "hidden") {
      if (!this.simulation.getUiProjection().paused)
        this.simulation.enqueue({ type: "pause-run" });
      this.audio.pauseAll();
      this.renderer?.pause();
      void this.checkpoint(true);
    } else {
      this.renderer?.resume();
      if (this.state.audioUnlocked) this.audio.resumeAll();
      this.refreshState();
    }
  };
}
